/* eslint-disable valid-jsdoc */
import { Activity } from 'botframework-directlinejs';
import { type BotActivity } from '@bingads-webui/ads-copilot-shared-types';
import { isTroubleshootingEvent, isAccountSwitchReaction } from '../../utils';

const NO_EXISTING_ACTIVITY_INDEX = -1;

function isUserMessage(activity: Activity, username: string): boolean {
  return activity.type === 'message' && activity.from?.name === username;
}

function isActivityReplaceable(existingActivity: BotActivity, username: string) {
// 1. the activity is a message not sent by the user
// 2. the activity is an troubleshooting event
  if (((existingActivity.type === 'message' && existingActivity.from?.name !== username) || isTroubleshootingEvent(existingActivity))) {
    if ((existingActivity.value as any) === 'AnimationCard' && ((existingActivity as any).attachments?.[0] as any)?.content?.text !== 'GeneratingAnswers') {
      // only GeneratingAnswers animation card can be replaced
      return false;
    }
    return true;
  }

  return false;
}

/**
 * Searches through the final activities from newest to oldest to find an existing activity that can be replaced with the new activity.
 * 1. This is used for streaming messages, the bot will send us a new message with more text to overwrite the previous message.
 * 2. This is used for troubleshooting engaged event, the bot will send us a new message to replace the previous message.
 * 3. This is used for ad delivery plugin event, the event should be replace by following up troubleshooting engage event.
 * @param finalActivities - List of activities to search through.
 * @param activity - New activity that was just received.
 * @param username - Used to identify if this message was sent by the user.
 * @returns Index of the message to replace, -1 if not found.
 */
export function findExistingActivityIndex(finalActivities: Activity[], activity: Activity, username: string): number {
  // 1. activity is a message and not be sent by the user.
  // 2. activity is an troubleshooting related event.
  // 3. activity is an account switch event (for dedup).
  if ((activity.type !== 'message' || activity.from?.name === username)
    && !isTroubleshootingEvent(activity)
    && !isAccountSwitchReaction(activity)) {
    return NO_EXISTING_ACTIVITY_INDEX;
  }

  // Account switch event dedup: keep only the latest one per user message segment
  if (isAccountSwitchReaction(activity)) {
    for (let i = finalActivities.length - 1; i >= 0; i -= 1) {
      if (isUserMessage(finalActivities[i], username)) {
        return NO_EXISTING_ACTIVITY_INDEX;
      }
      if (isAccountSwitchReaction(finalActivities[i])) {
        return i;
      }
    }
    return NO_EXISTING_ACTIVITY_INDEX;
  }

  const botActivity = activity as BotActivity;

  // 1. if it's not a troubleshooting event, activity must be a reply to an earlier message
  // 2. if it's a troubleshooting event, activity.replyToId could be undefined
  if (!botActivity.replyToId && !isTroubleshootingEvent(botActivity)) {
    return NO_EXISTING_ACTIVITY_INDEX;
  }

  // iterate through final activities from newest to oldest
  for (let i = finalActivities.length - 1; i >= 0; i -= 1) {
    const currentActivity = finalActivities[i] as BotActivity;

    if (((currentActivity.replyToId === botActivity.replyToId && botActivity.replyToId !== undefined)
        || (isTroubleshootingEvent(botActivity) && currentActivity.id === botActivity.replyToId && botActivity.replyToId !== undefined))
       && isActivityReplaceable(currentActivity, username)) {
      if (botActivity.value === 'ConnectToAgent' && (currentActivity.value as any) !== 'AnimationCard') {
        // Sometimes bot will send ConnectToAgent immediately after GeneratingAnswers, sometimes it will start replying with a normal message before
        // sending ConnectToAgent. Normal message should not be replaced with ConnectToAgent.
        return NO_EXISTING_ACTIVITY_INDEX;
      }

      // found an existing activity that can be replaced with the new activity
      return i;
    }
  }

  return NO_EXISTING_ACTIVITY_INDEX;
}
